import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RequestReportForm from './RequestReportForm';

// Single source of truth for whether the current user can access Reports.
// Matches the server's requireReportsAccess middleware: any paid distributor
// (starter / team / pro / enterprise-when-activated) gets access; suppliers get the
// "coming soon" surface; free distributors get the upgrade surface.
export function useReportsAccess() {
  const { supplierInfo, billing } = useAuth();
  if (!supplierInfo) return { status: 'loading' };
  if (supplierInfo.is_staff) return { status: 'allowed' };
  if (!billing) return { status: 'loading' };
  if (billing.is_override) return { status: 'allowed' };
  if (supplierInfo.account_type !== 'Distributor') return { status: 'supplier' };
  const plan = String(billing.plan || '').toLowerCase();
  const enterpriseOk = plan === 'enterprise' ? !!billing.enterprise_activated : true;
  const isPaid = ['starter', 'team', 'pro', 'enterprise'].includes(plan) && enterpriseOk;
  return { status: isPaid ? 'allowed' : 'upgrade' };
}

function GateShell({ children }) {
  return (
    <div style={{ background: '#fafafa', minHeight: '100vh' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 16px 64px' }}>
        {children}
      </div>
    </div>
  );
}

function SupplierComingSoon() {
  return (
    <GateShell>
      <div className="text-center mb-4">
        
        <h1 className="fw-bold mb-2" style={{ fontSize: 28, letterSpacing: -0.3 }}>
          Reports for suppliers  coming soon
        </h1>
        <p className="text-muted mb-0" style={{ fontSize: 15 }}>
          We're building supplier-facing reports. Tell us what would help you most
          and we'll prioritise based on demand.
        </p>
      </div>

      <RequestReportForm
        heading="Request a report"
        helper="Tell us what data you want and how you'd use it."
      />
    </GateShell>
  );
}

function UpgradePrompt() {
  return (
    <GateShell>
      <div className="text-center mb-4">
        <h1 className="fw-bold mb-2" style={{ fontSize: 28, letterSpacing: -0.3 }}>
          Reports is a paid feature
        </h1>
        <p className="text-muted mb-0" style={{ fontSize: 15 }}>
          Send branded customer footprint emails, build a customer book, and unlock
          new report types as they launch.
        </p>
      </div>

      <div
        style={{
          background: '#fff',
          border: '1px solid #e4e4e7',
          borderRadius: 14,
          padding: 24,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)',
        }}
      >
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 14 }}>
          {[
            'Customer Footprint Reports - send emails with product images and per-product CO₂e',
            'Saved customer book with full history and resend',
            'Public, link-shareable product results pages built into every report',
            'More report types as they ship',
          ].map((line) => (
            <li key={line} className="d-flex gap-2 align-items-start py-2" style={{ borderBottom: '1px solid #f4f4f5' }}>
              <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
              <span style={{ color: '#18181b' }}>{line}</span>
            </li>
          ))}
        </ul>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Link to="/billing" className="btn btn-dark">View pricing</Link>
        </div>
      </div>
    </GateShell>
  );
}

function GateLoading() {
  return (
    <GateShell>
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    </GateShell>
  );
}

export default function ReportsGate({ children }) {
  const access = useReportsAccess();
  if (access.status === 'loading') return <GateLoading />;
  if (access.status === 'supplier') return <SupplierComingSoon />;
  if (access.status === 'upgrade') return <UpgradePrompt />;
  return children;
}
